import { TableBody as MuiTableBody, TableRow, TableCell } from '@mui/material';
import { TableColumn } from '../Table.props';
import { TableDataRow } from './TableDataRow';
import { EmptyState } from '../states/EmptyState';
import { LoadingState } from '../states/LoadingState';

export interface TableBodyProps<T> {
  rows: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  /** Custom loading component to display while data is being fetched */
  loadingComponent?: React.ReactNode;
  /** Custom empty state component to display when no data is available */
  noDataComponent?: React.ReactNode;
  selectedRowIds: Set<string | number>;
  onRowClick?: (row: T, index: number) => void;
  getRowId: (row: T) => string | number;
  hover?: boolean;
  striped?: boolean;
  columnStickyPositions?: Map<string | keyof T, number>;
}

/**
 * TableBody - Renders the complete table body section
 *
 * Features:
 * - Handles loading state with custom loading components
 * - Handles empty state with custom empty components
 * - Renders data rows with proper selection and click handling
 * - Manages row selection state
 * - Dynamic height calculation
 * - Proper composition of all body elements
 */
export const TableBody = <T,>({
  rows,
  columns,
  loading,
  loadingComponent,
  noDataComponent,
  selectedRowIds,
  onRowClick,
  getRowId,
  hover,
  striped,
  columnStickyPositions,
}: TableBodyProps<T>) => {
  return (
    <MuiTableBody>
      {loading ? (
        loadingComponent ? (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              sx={{ padding: 0, border: 'none' }}
            >
              {loadingComponent}
            </TableCell>
          </TableRow>
        ) : (
          <LoadingState columnsCount={columns.length} />
        )
      ) : rows.length === 0 ? (
        noDataComponent ? (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              sx={{ padding: 0, border: 'none' }}
            >
              {noDataComponent}
            </TableCell>
          </TableRow>
        ) : (
          <EmptyState colSpan={columns.length} />
        )
      ) : (
        rows.map((row, index) => {
          const rowId = getRowId(row);
          const isSelected = selectedRowIds.has(rowId);

          return (
            <TableDataRow
              key={String(rowId) || index}
              row={row}
              index={index}
              columns={columns}
              isSelected={isSelected}
              onRowClick={onRowClick}
              hover={hover}
              striped={striped}
              columnStickyPositions={columnStickyPositions}
            />
          );
        })
      )}
    </MuiTableBody>
  );
};
